import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import Unauthorized from "@/pages/Unauthorized";
import Dashboard from "@/pages/Dashboard";
import Matters from "@/pages/Matters";
import MatterDetail from "@/pages/MatterDetail";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Tasks from "@/pages/Tasks";
import Calendar from "@/pages/Calendar";
import CauseLists from "@/pages/CauseLists";
import CourtSearch from "@/pages/CourtSearch";
import Billing from "@/pages/Billing";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Clauses from "@/pages/Clauses";
import Analytics from "@/pages/Analytics";
import Precedents from "@/pages/Precedents";
import Playbooks from "@/pages/Playbooks";
import Compliance from "@/pages/Compliance";
import Research from "@/pages/Research";
import Drafting from "@/pages/Drafting";
import DraftDetail from "@/pages/DraftDetail";
import Documents from "@/pages/Documents";
import DocumentDetail from "@/pages/DocumentDetail";
import Vault from "@/pages/Vault";
import Settings from "@/pages/Settings";
import Chambers from "@/pages/Chambers";
import ChamberDetail from "@/pages/ChamberDetail";
import Admin from "@/pages/Admin";

function ProtectedLayout({ children, requireAdmin = false }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center parchment grain">
                <div className="font-serif text-xl">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (requireAdmin && !["admin", "superadmin"].includes(user.role)) return <Navigate to="/dashboard" replace />;
    return <AppShell>{children}</AppShell>;
}

function RouterGate() {
    const location = useLocation();
    // Synchronously handle #session_id=... before anything else
    if (location.hash && location.hash.includes("session_id=")) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/login" element={<LoginOrRedirect />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
            <Route path="/matters" element={<ProtectedLayout><Matters /></ProtectedLayout>} />
            <Route path="/matters/:id" element={<ProtectedLayout><MatterDetail /></ProtectedLayout>} />
            <Route path="/clients" element={<ProtectedLayout><Clients /></ProtectedLayout>} />
            <Route path="/clients/:id" element={<ProtectedLayout><ClientDetail /></ProtectedLayout>} />
            <Route path="/tasks" element={<ProtectedLayout><Tasks /></ProtectedLayout>} />
            <Route path="/calendar" element={<ProtectedLayout><Calendar /></ProtectedLayout>} />
            <Route path="/cause-lists" element={<ProtectedLayout><CauseLists /></ProtectedLayout>} />
            <Route path="/court-search" element={<ProtectedLayout><CourtSearch /></ProtectedLayout>} />
            <Route path="/billing" element={<ProtectedLayout><Billing /></ProtectedLayout>} />
            <Route path="/billing/invoices/:id" element={<ProtectedLayout><InvoiceDetail /></ProtectedLayout>} />
            <Route path="/clauses" element={<ProtectedLayout><Clauses /></ProtectedLayout>} />
            <Route path="/analytics" element={<ProtectedLayout><Analytics /></ProtectedLayout>} />
            <Route path="/precedents" element={<ProtectedLayout><Precedents /></ProtectedLayout>} />
            <Route path="/playbooks" element={<ProtectedLayout><Playbooks /></ProtectedLayout>} />
            <Route path="/compliance" element={<ProtectedLayout><Compliance /></ProtectedLayout>} />
            <Route path="/research" element={<ProtectedLayout><Research /></ProtectedLayout>} />
            <Route path="/drafting" element={<ProtectedLayout><Drafting /></ProtectedLayout>} />
            <Route path="/drafting/:id" element={<ProtectedLayout><DraftDetail /></ProtectedLayout>} />
            <Route path="/documents" element={<ProtectedLayout><Documents /></ProtectedLayout>} />
            <Route path="/documents/:id" element={<ProtectedLayout><DocumentDetail /></ProtectedLayout>} />
            <Route path="/vault" element={<ProtectedLayout><Vault /></ProtectedLayout>} />
            <Route path="/chambers" element={<ProtectedLayout><Chambers /></ProtectedLayout>} />
            <Route path="/chambers/:id" element={<ProtectedLayout><ChamberDetail /></ProtectedLayout>} />
            <Route path="/admin" element={<ProtectedLayout requireAdmin><Admin /></ProtectedLayout>} />
            <Route path="/settings" element={<ProtectedLayout><Settings /></ProtectedLayout>} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function LoginOrRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;
    return <Login />;
}

function HomeRoute() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;
    return <Landing />;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <RouterGate />
                    <Toaster position="bottom-right" richColors closeButton />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
